import React, { useState } from "react";
import {
    Box,
    Button,
    Drawer,
    Typography,
    Chip,
    TextField,
    alpha,
    InputAdornment,
    useTheme,
    Divider,
} from "@mui/material";
import BorderedBox from "@convin/components/custom_components/BorderedBox";
import { CloseSvg } from "@convin/components/svg";
import { Search } from "@mui/icons-material";

interface FlexItemsWithOverflowProps {
    items: string[];
    maxItems?: number;
    itemComponent?: (item: string, index: number) => React.ReactNode;
    drawerTitle?: React.ReactNode;
    drawerComponent?: (items: string[]) => React.ReactNode;
}

const FlexItemsWithOverflow: React.FC<FlexItemsWithOverflowProps> = ({
    items,
    maxItems = 4,
    itemComponent,
    drawerTitle = "All Items",
    drawerComponent,
}) => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const theme = useTheme();

    // Default item renderer
    const defaultItemRenderer = (item: string, index: number) => (
        <Chip
            key={index}
            label={item}
            variant="filled"
            sx={{
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
                fontSize: theme.typography.pxToRem(14),
            }}
        />
    );
    const defaultDrawerComponent = (items: string[]) => (
        <div className="flex flex-col gap-3 flex-1 overflow-auto">
            {items.map((item, index) => (
                <BorderedBox
                    key={index}
                    p={1.5}
                    sx={{
                        border: "1px solid rgba(153, 153, 153, 0.20)",
                        borderRadius: "4px",
                        boxShadow: "none",
                    }}
                >
                    <Typography variant="textSm" color="textColors.333">
                        {item}
                    </Typography>
                </BorderedBox>
            ))}
        </div>
    );
    const visibleItems = items.slice(0, maxItems);
    const hiddenItems = items.length > maxItems ? items.slice(maxItems) : [];
    const renderItem = itemComponent || defaultItemRenderer;
    const renderDrawerComponent = drawerComponent || defaultDrawerComponent;

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    alignItems: "center",
                    overflow: "hidden",
                }}
            >
                {visibleItems.map((item, index) => (
                    <>{renderItem(item, index)}</>
                ))}
                {hiddenItems.length > 0 && (
                    <Chip
                        label={`+${hiddenItems.length}`}
                        variant="outlined"
                        component="button"
                        onClick={() => setDrawerOpen(true)}
                        sx={{
                            bgcolor: (theme) =>
                                alpha(theme.palette.primary.main, 0.1),
                            color: "primary.main",
                            fontSize: theme.typography.pxToRem(14),
                        }}
                    />
                )}
            </Box>
            <Drawer
                anchor="right"
                open={drawerOpen}
                sx={{
                    zIndex: 10000,
                }}
                slotProps={{
                    paper: {
                        sx: {
                            minWidth: "276px",
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Box
                        p={1.5}
                        className="flex justify-between items-center flex-shrink-0"
                    >
                        <Typography variant="h6">{drawerTitle}</Typography>
                        <Button
                            variant="text"
                            sx={{
                                color: "textColors.333",
                            }}
                            onClick={() => setDrawerOpen(false)}
                        >
                            <CloseSvg />
                        </Button>
                    </Box>
                    <Divider />
                    <Box
                        p={1.5}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            flexWrap: "wrap",
                            gap: 1,
                            flex: 1,
                            overflow: "auto",
                        }}
                    >
                        <div className="flex-shrink-0">
                            <TextField
                                fullWidth
                                placeholder="Search"
                                value={searchQuery}
                                sx={{
                                    ".MuiInputBase-input": {
                                        p: 1.5,
                                        fontSize: theme.typography.pxToRem(14),
                                        py: 0.5,
                                    },
                                    backgroundColor: alpha(
                                        theme.palette.textColors[999],
                                        0.1
                                    ),
                                }}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment
                                                position="start"
                                                sx={{
                                                    m: "0px !important",
                                                }}
                                            >
                                                <Search
                                                    sx={{
                                                        color: "textColors.999",
                                                        height: 16,
                                                        width: 16,
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        </div>
                        {renderDrawerComponent(
                            items.filter((item) =>
                                item
                                    .toLowerCase()
                                    .includes(searchQuery.toLowerCase())
                            )
                        )}
                    </Box>
                </Box>
            </Drawer>
        </>
    );
};

export default FlexItemsWithOverflow;
